<script setup lang="ts">
import { ProductCodeToNameMap, BannerType } from "../utils/types";
import { DecisionType } from "../models";
import StatusPill from "./StatusPill.vue";
import {
  formatDecisionStatus,
  formatDecisionActionMessage,
  formatDecisionTime,
} from "../utils/decisionStatus";
import { DecisionStatusEnum } from "../api.generated";
import Banner from "./Banner.vue";
import { TwoButton, TwoCheckbox } from "@wegift/two-components";
import { caseReferenceFormatter } from "../utils/caseReferenceFormatter";
import { getProductName } from "../stores/product";

const emit = defineEmits(["productToggled", "handleViewCaseHistory"]);

const props = defineProps<{
  shouldDisplayTitle: boolean;
  bannerMessage: string | null;
  decisionList: DecisionType[];
  selectedProductCodes: string[];
  productCodesAndNames: ProductCodeToNameMap;
  showCaseReference?: boolean;
}>();

function handleViewCaseHistory(caseReference: string) {
  emit("handleViewCaseHistory", caseReference);
}
</script>

<template>
  <div v-if="shouldDisplayTitle" class="title-margin text-lg font-semibold">
    {{ formatDecisionStatus(decisionList[0].decision) }}
  </div>

  <Banner
    v-if="!!bannerMessage"
    :type="BannerType.Info"
    :message="bannerMessage"
  ></Banner>
  <div class="divide-y rounded-lg bg-white p-4 shadow-xl">
    <div
      class="flex items-center p-2"
      v-for="decision in decisionList"
      :key="decision.productCode"
      @click="$emit('productToggled', decision.productCode)"
    >
      <div class="pointer-events-none">
        <TwoCheckbox
          v-if="decision.decision === DecisionStatusEnum.PENDING"
          :value="decision.productCode"
          v-model="props.selectedProductCodes"
        />

        <div v-else="" id="spacer" class="mr-4 h-4 w-4" />
      </div>
      <div class="mr-4 w-60 flex-none truncate text-sm font-semibold">
        <span
          :title="
            getProductName(props.productCodesAndNames, decision.productCode)
          "
          >{{
            getProductName(props.productCodesAndNames, decision.productCode)
          }}</span
        >
      </div>
      <div class="mr-4 flex min-w-0 flex-1">
        <StatusPill
          class="shrink-0"
          :decision="decision.decision"
          :key="decision.decision"
        />
        <span
          v-if="
            [
              DecisionStatusEnum.PENDING,
              DecisionStatusEnum.APPROVED,
              DecisionStatusEnum.DENIED,
            ].indexOf(decision.decision) > -1
          "
          class="flex min-w-0 items-center text-center"
        >
          {{ formatDecisionActionMessage(decision) }}
        </span>
        <span class="flex min-w-0 flex-1 items-center text-center">
          <span
            v-if="
              decision.decision === DecisionStatusEnum.INFORMATION_REQUESTED &&
              decision.overrideReason
            "
            class="truncate"
            :title="decision.overrideReason"
          >
            “{{ decision.overrideReason }}”
          </span>
        </span>
        <span
          v-if="decision.decision === DecisionStatusEnum.INFORMATION_REQUESTED"
          class="flex min-w-0 flex-1 items-center text-center"
        >
          {{ formatDecisionActionMessage(decision) }}
        </span>
        <span class="flex min-w-0 flex-1 items-center text-center">
          <span
            v-if="
              decision.overrideReason &&
              decision.decision !== DecisionStatusEnum.INFORMATION_REQUESTED
            "
            class="truncate"
            :title="decision.overrideReason"
          >
            “{{ decision.overrideReason }}”
          </span>
          <span
            class="ml-1 truncate text-sm"
            v-if="decision.caseReference"
            :title="decision.caseReference"
          >
            Case Ref: {{ caseReferenceFormatter(decision.caseReference) }}
          </span>
        </span>
      </div>
      <two-button
        v-if="showCaseReference && decision.caseReference"
        class="btn-primary"
        @click="handleViewCaseHistory(decision.caseReference as string)"
      >
        View case history
      </two-button>
    </div>
  </div>
</template>
<style scoped>
.title-margin {
  margin-top: 25px;
  margin-bottom: 25px;
}
</style>
